// Template legal texts: the operator should have them reviewed by a lawyer before relying on them.
import { RETENTION, SITE, SUB_PROCESSORS, TERMS_VERSION } from "@clinic/shared";
import type { LegalDoc, LegalDocId } from "./types";

const { legalName, cvr, address, email } = SITE.company;
/** Who "we" are: the legal entity if known, otherwise the service name. */
const operator = legalName || SITE.name;
const host = SITE.origin.replace(/^https?:\/\//, "");

const companyDetails = [cvr && `CVR ${cvr}`, address, email && `email ${email}`].filter(Boolean).join("; ");

/** How to reach us, without inventing an address that does not exist. */
const contactUs = email ? `contact us at ${email}` : "contact us via the details above";
const contactUsElsewhere = email ? `contact us at ${email}` : "contact us via the details in the privacy policy";

const whoWeAre: string[] = [
  `${SITE.name} (${host}) is an online booking service for independent clinics${legalName ? `, operated by ${legalName}` : ""}. In these texts, "we" and "us" means ${operator}.`,
  ...(companyDetails ? [`Company details: ${companyDetails}.`] : []),
];

const purposeLabel: Record<(typeof SUB_PROCESSORS)[number]["purpose"], string> = {
  email: "Sending emails (confirmations, reminders, password reset, invitations)",
  sms: "Sending SMS reminders, if the clinic enables them",
  hosting: "Hosting the application, database and backups",
};

const subProcessorTable = {
  head: ["Sub-processor", "Purpose", "Location"],
  rows: SUB_PROCESSORS.map((p) => [p.name, purposeLabel[p.purpose], p.location]),
};

const backupNote =
  "Database backups are kept for up to 12 months (30 daily and 12 monthly copies) and are then overwritten. Data that has been erased from the live system can therefore remain in backups until they are overwritten; backups are only used to restore the service.";

const privacy: LegalDoc = {
  title: "Privacy policy",
  description: `How ${SITE.name} handles personal data for clinics, their customers and staff: what we collect, why, how long we keep it and your rights.`,
  intro: `Last updated ${TERMS_VERSION}. This policy explains how personal data is handled when you book an appointment through ${SITE.name}, when you work at a clinic that uses it, and when you visit ${host}.`,
  sections: [
    {
      heading: "Who we are",
      paragraphs: whoWeAre,
    },
    {
      heading: "Our role: the clinic decides, we process",
      paragraphs: [
        "When you book with a clinic, that clinic is the data controller for your booking data. It decides what the data is used for, and we process it on the clinic's behalf as its data processor under a data processing agreement.",
        "For clinic staff accounts, the admin console and visits to our website, we are the data controller ourselves.",
        "If you have booked an appointment and have a question about your data, the clinic you booked with is your first point of contact.",
      ],
    },
    {
      heading: "Data about customers who book",
      paragraphs: ["For each clinic, we store the following about its customers:"],
      list: [
        "Full name and email address",
        "Phone number, if you give one",
        "Your bookings: service, practitioner, time and status",
        "An optional note to the clinic, if you write one",
        "The reason for a cancellation, if one is given",
        "Records of SMS reminders sent to you (phone number and delivery status), if the clinic uses SMS",
      ],
    },
    {
      heading: "Health information",
      paragraphs: [
        "The booking form asks you not to write sensitive health information in the note. Even so, the fact that you have booked a health-related service, such as physiotherapy or chiropractic treatment, may itself reveal information about your health (GDPR article 9).",
        "The clinic is responsible for having a legal basis for this, typically that the processing is necessary for health care or treatment (article 9(2)(h)) or another basis that applies to the clinic. We process the data only on the clinic's instructions and do not use it for anything else.",
      ],
    },
    {
      heading: "Data about clinic staff and administrators",
      list: [
        "Name, email address and role at the clinic",
        "Your password, stored only as a hash",
        "Login sessions, including IP address and browser type, used to keep your account secure",
        "Password-reset links and invitations while they are valid",
        "For the admin console: the administrator's email address, login sessions with IP address, and an audit log of actions taken",
      ],
    },
    {
      heading: "Website visitors",
      paragraphs: [
        "We do not use analytics, advertising or tracking of any kind, and we load no third-party scripts or fonts. Like any website, our server needs your IP address to deliver the page to you. The few cookies we use are described in the cookie policy.",
      ],
    },
    {
      heading: "Why we process data and on what legal basis",
      table: {
        head: ["Purpose", "Legal basis"],
        rows: [
          [
            "Handling bookings, confirmations and reminders for a clinic",
            "The clinic's own legal basis, as controller; we act on its instructions (GDPR article 28)",
          ],
          [
            "Providing staff accounts and the service to clinics",
            "Performance of our contract with the clinic (article 6(1)(b))",
          ],
          [
            "Keeping accounts and the service secure, including session and audit logs",
            "Our legitimate interest in protecting the service and its users (article 6(1)(f))",
          ],
          ["Keeping records required by accounting and other law", "Legal obligation (article 6(1)(c))"],
        ],
      },
    },
    {
      heading: "Emails, SMS and calendar feeds",
      paragraphs: [
        "We send booking confirmations, notices when a booking is changed or cancelled, reminders 24 hours before an appointment, password-reset emails and invitations. Emails are sent through Amazon SES in the EU.",
        "If a clinic enables SMS reminders, your phone number is sent to our SMS provider, GatewayAPI in Denmark, to deliver them.",
        "A clinic can let its practitioners subscribe to a private calendar feed with their appointments. The feed contains the customer's name and phone number, but not the note. The clinic decides whether to use this; if a practitioner subscribes, the data is passed to the calendar provider the practitioner has chosen.",
      ],
    },
    {
      heading: "Sub-processors and transfers",
      paragraphs: [
        "We use the following sub-processors. Each is bound by a data processing agreement.",
        "All data is hosted in the EU/EEA, and we do not transfer it outside the EU/EEA. If a sub-processor ever needs to, the transfer will be covered by the EU Commission's standard contractual clauses or another valid transfer mechanism.",
      ],
      table: subProcessorTable,
    },
    {
      heading: "How long we keep data",
      table: {
        head: ["Data", "Retention"],
        rows: [
          [
            "Customers",
            `Anonymised ${RETENTION.customerMonths} months after their last booking, or after they were created if they never booked`,
          ],
          [
            "Expired login sessions, reset links and invitations",
            `Deleted ${RETENTION.expiredSessionDays} days after they expire`,
          ],
          ["SMS delivery records", `${RETENTION.smsMonths} months`],
          ["Admin console audit log", `${RETENTION.auditMonths} months`],
          [
            "A clinic's data after its subscription ends",
            "Deleted within 90 days after the agreement ends, unless the clinic asks for deletion sooner",
          ],
        ],
      },
      paragraphs: [
        "A clinic can also export or erase an individual customer's data at any time from its booking list.",
        backupNote,
      ],
    },
    {
      heading: "How we protect data",
      list: [
        "All traffic is encrypted in transit (HTTPS/TLS)",
        "Passwords are hashed with a modern password hashing algorithm and are never stored in readable form",
        "Each clinic's data is isolated from other clinics' data, enforced in the database itself (row-level security)",
        "Access to the admin console is logged",
        "Regular backups",
      ],
    },
    {
      heading: "Your rights",
      paragraphs: ["Under the GDPR you have the right to:"],
      list: [
        "Access the personal data held about you",
        "Have inaccurate data corrected",
        "Have your data erased",
        "Restrict the processing of your data",
        "Receive your data in a portable format",
        "Object to processing based on legitimate interest",
      ],
    },
    {
      heading: "How to use your rights",
      paragraphs: [
        "If you have booked with a clinic, contact that clinic, as it is the controller of your booking data. We help clinics answer such requests, and clinics have tools to export and erase a customer's data.",
        `For staff accounts, the admin console or our website, ${contactUs}.`,
      ],
    },
    {
      heading: "Complaints",
      paragraphs: [
        "You can complain to the Danish Data Protection Agency (Datatilsynet), www.datatilsynet.dk. We would appreciate the chance to resolve your concern first.",
      ],
    },
    {
      heading: "Changes to this policy",
      paragraphs: [
        "We update this policy when the service or the law changes. The date at the top shows when it was last updated.",
      ],
    },
  ],
};

const terms: LegalDoc = {
  title: "Terms of service",
  description: `The terms that apply when a clinic subscribes to and uses ${SITE.name}, the online booking service for independent clinics.`,
  intro: `Version ${TERMS_VERSION}. These terms apply between ${operator} and each clinic that uses ${SITE.name}. They are accepted, together with the data processing agreement, when the clinic is created.`,
  sections: [
    {
      heading: "About us",
      paragraphs: whoWeAre,
    },
    {
      heading: "Who these terms are for",
      paragraphs: [
        `${SITE.name} is a business service for clinics and other independent practitioners. By creating a clinic, you confirm that you are acting on behalf of a business and have the authority to accept these terms for it. Consumer protection rules for consumer purchases do not apply.`,
        "The clinic's customers book through the service but are not party to these terms. Their relationship is with the clinic, and the clinic answers its customers' questions about bookings, treatments and payment.",
      ],
    },
    {
      heading: "The service",
      paragraphs: [
        `${SITE.name} lets a clinic publish its services and opening hours, take online bookings, send confirmations and reminders to its customers, and manage its staff and calendar. We may improve and change the service over time, as long as its core features remain available.`,
      ],
    },
    {
      heading: "Accounts and access",
      paragraphs: [
        "The clinic is responsible for who it invites as staff, for the roles it gives them, and for everything done under its accounts. Each person must have their own login and keep their password confidential.",
        `Tell us without delay if you suspect that an account has been misused; ${contactUs}.`,
      ],
    },
    {
      heading: "The clinic's responsibilities",
      paragraphs: [
        "The clinic is solely responsible for the services it offers, its prices, its cancellation policy and its relationship with its customers, including the treatment it provides and any payment between the clinic and its customers.",
        "As data controller for its customers' data, the clinic is responsible for having a legal basis for the processing and for informing its customers, as described in the data processing agreement.",
      ],
    },
    {
      heading: "Acceptable use",
      paragraphs: ["You must not use the service to:"],
      list: [
        "Break the law or infringe the rights of others",
        "Send spam or messages that customers have not asked for",
        "Upload malicious code or try to gain access to other clinics' data or our systems",
        "Overload, probe or disrupt the service, or circumvent its security or usage limits",
        "Resell or make the service available to third parties without our written consent",
      ],
    },
    {
      heading: "Prices and payment",
      paragraphs: [
        "The price is the plan agreed when you subscribed, or as shown in the app. We give at least 30 days' notice of price changes, and you can terminate before they take effect.",
      ],
    },
    {
      heading: "Availability and support",
      paragraphs: [
        "We make every reasonable effort to keep the service available and working, but we provide it on a best-effort basis and cannot guarantee uninterrupted or error-free operation. Planned maintenance is done outside normal clinic hours where possible.",
      ],
    },
    {
      heading: "Data and data processing",
      paragraphs: [
        "The clinic owns its data. We never use the clinic's or its customers' data for our own purposes, such as marketing, and we never sell it. Our processing of personal data on the clinic's behalf is governed by the data processing agreement, which forms part of these terms. Our own processing is described in the privacy policy.",
        "The clinic can export an individual customer's data at any time. When the agreement ends, the clinic's data is deleted within 90 days, unless the clinic asks for deletion sooner. Backups are overwritten within 12 months.",
      ],
    },
    {
      heading: "Liability",
      paragraphs: [
        "Our total liability under these terms is limited to the fees the clinic has paid us in the 12 months before the event giving rise to the claim.",
        "We are not liable for indirect loss, including lost profit, lost bookings or loss of goodwill. Nor are we liable for the clinic's services or its dealings with its customers.",
        "These limitations do not apply where liability cannot be limited under Danish law, such as for gross negligence or intent.",
      ],
    },
    {
      heading: "Changes to these terms",
      paragraphs: [
        "We may change these terms. We will notify the clinic by email at least 30 days before material changes take effect. If you do not accept the changes, you can terminate before they take effect; continuing to use the service afterwards means you accept them.",
      ],
    },
    {
      heading: "Term and termination",
      paragraphs: [
        "The agreement runs until terminated. Either party may terminate it with effect from the end of the current subscription period. We may suspend or terminate access immediately if the clinic seriously breaches these terms, for example by misusing the service or not paying.",
      ],
    },
    {
      heading: "Governing law and disputes",
      paragraphs: [
        "These terms are governed by Danish law. Any dispute that cannot be resolved by agreement shall be settled by the Danish courts, with the Copenhagen City Court (Københavns Byret) as the court of first instance.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: [`For questions about these terms, ${contactUs}.`],
    },
  ],
};

const dpa: LegalDoc = {
  title: "Data processing agreement",
  description: `The data processing agreement (GDPR article 28) between ${SITE.name} and each clinic that uses the service for its customers' bookings.`,
  intro: `Version ${TERMS_VERSION}. This data processing agreement sets out how ${operator} processes personal data on behalf of clinics using ${SITE.name}. The clinic accepts it together with the terms of service when the clinic is created.`,
  sections: [
    {
      heading: "Parties",
      paragraphs: [
        `The clinic is the data controller ("the controller"). ${operator} is the data processor ("the processor"). The agreement fulfils the requirements of article 28 of the General Data Protection Regulation (GDPR).`,
        ...(companyDetails ? [`Processor details: ${companyDetails}.`] : []),
      ],
    },
    {
      heading: "Subject matter and duration",
      paragraphs: [
        `The processor processes personal data to provide ${SITE.name} to the controller. The agreement applies for as long as the processor processes personal data on behalf of the controller, and ends automatically when the terms of service end and the data has been deleted.`,
      ],
    },
    {
      heading: "Nature and purpose of the processing",
      paragraphs: [
        "Storage, organisation, retrieval, transmission and deletion of data in order to let the controller's customers book appointments online, to send them confirmations, change notices and reminders by email and, if enabled, by SMS, and to let the controller's staff manage bookings and calendars.",
      ],
    },
    {
      heading: "Categories of data subjects",
      list: ["The controller's customers (people who book or are booked)", "The controller's staff and practitioners"],
    },
    {
      heading: "Types of personal data",
      list: [
        "Customers: name, email address, optional phone number, bookings (service, practitioner, time, status), optional note to the clinic, cancellation reason and SMS delivery records",
        "Staff: name, email address, role, password hash, login sessions with IP address and browser type, password-reset links and invitations",
        "Special categories: a booking for a health-related service may reveal information about the customer's health (GDPR article 9). The controller must not ask customers to enter further health information in free-text fields",
      ],
    },
    {
      heading: "Instructions",
      paragraphs: [
        "The processor processes personal data only on documented instructions from the controller. The controller's use of the service and the settings it chooses there, together with this agreement, constitute those instructions. Processing required by EU or Danish law is exempt; the processor will inform the controller of such a requirement unless the law forbids it.",
        "The processor does not process the data for its own purposes. The processor will notify the controller if, in its opinion, an instruction infringes data protection law.",
      ],
    },
    {
      heading: "Confidentiality",
      paragraphs: [
        "Only people who need access to provide the service are given access to the controller's data. They are bound by confidentiality, either by contract or by law.",
      ],
    },
    {
      heading: "Security",
      paragraphs: [
        "The processor implements appropriate technical and organisational measures under GDPR article 32, including:",
      ],
      list: [
        "Encryption of all data in transit (HTTPS/TLS)",
        "Passwords hashed with a modern password hashing algorithm",
        "Isolation of each clinic's data, enforced in the database (row-level security)",
        "Logging of access to the admin console",
        "Regular backups, kept for up to 12 months",
        "Automatic deletion and anonymisation according to the retention periods in the privacy policy",
      ],
    },
    {
      heading: "Sub-processors",
      paragraphs: [
        "The controller gives the processor general authorisation to use sub-processors. The sub-processors currently used are listed below.",
        "The processor will notify the controller by email at least 30 days before adding or replacing a sub-processor. The controller may object on reasonable data protection grounds; if the parties cannot agree, the controller may terminate the service before the change takes effect.",
        "The processor imposes the same data protection obligations on each sub-processor as in this agreement and remains responsible to the controller for the sub-processor's compliance.",
      ],
      table: subProcessorTable,
    },
    {
      heading: "Transfers outside the EU/EEA",
      paragraphs: [
        "Personal data is processed within the EU/EEA. The processor will not transfer it outside the EU/EEA unless the controller is notified in advance and the transfer is covered by the EU Commission's standard contractual clauses or another valid transfer mechanism under GDPR chapter V.",
      ],
    },
    {
      heading: "Assistance to the controller",
      paragraphs: [
        "Taking into account the nature of the processing, the processor assists the controller in responding to requests from data subjects. The service has tools that let the controller export or erase an individual customer's data from the booking list. The processor forwards any request it receives directly to the controller.",
        "The processor also assists the controller in meeting its obligations under articles 32 to 36 (security, breach notification, data protection impact assessments and prior consultation), using the information available to it.",
      ],
    },
    {
      heading: "Personal data breaches",
      paragraphs: [
        "The processor notifies the controller without undue delay, and at the latest 48 hours after becoming aware of a personal data breach. The notification describes, as far as is known, the nature of the breach, the data and data subjects affected, the likely consequences and the measures taken, so that the controller can notify Datatilsynet within 72 hours where required.",
      ],
    },
    {
      heading: "Deletion and return of data",
      paragraphs: [
        "When the agreement ends, the controller can export its data before the end date. The processor then deletes the controller's personal data within 90 days, unless the controller asks for deletion sooner or the law requires it to be kept.",
        backupNote,
      ],
    },
    {
      heading: "Audits and information",
      paragraphs: [
        "On request, the processor makes available the information needed to demonstrate compliance with this agreement and article 28, and allows for and contributes to audits and inspections carried out by the controller or an auditor mandated by it. Audits must be announced reasonably in advance, and the controller bears its own costs.",
      ],
    },
    {
      heading: "The controller's obligations",
      list: [
        "Ensuring that there is a legal basis for the processing, including for any health information (GDPR articles 6 and 9)",
        "Giving instructions that comply with data protection law",
        "Informing its customers about the processing (articles 13 and 14), for example in its own privacy notice",
        "Deciding whether to use SMS reminders and calendar feeds, and on what basis",
      ],
    },
    {
      heading: "Liability and precedence",
      paragraphs: [
        "Each party's liability under this agreement is governed by the liability provisions of the terms of service, to the extent permitted by the GDPR. If this agreement conflicts with the terms of service on the protection of personal data, this agreement prevails.",
      ],
    },
  ],
};

const cookies: LegalDoc = {
  title: "Cookie policy",
  description: `${SITE.name} uses only strictly necessary cookies for login and remembers your language. No analytics, advertising or third-party tracking.`,
  intro: `Last updated ${TERMS_VERSION}. ${SITE.name} uses as few cookies as possible, and none for tracking.`,
  sections: [
    {
      heading: "What we store in your browser",
      table: {
        head: ["Name", "Purpose", "Duration", "Type"],
        rows: [
          ["__Host-cb_session", "Keeps clinic staff signed in", "7 days", "Strictly necessary cookie (HttpOnly)"],
          [
            "__Host-cb_admin",
            "Keeps administrators signed in to the admin console",
            "8 hours, and at most 24 hours from sign-in",
            "Strictly necessary cookie (HttpOnly)",
          ],
          ["cb_locale", "Remembers the language you have chosen", "Until you clear it", "Functional (local storage)"],
        ],
      },
      paragraphs: [
        "If you only book an appointment, no login cookie is set. The login cookies are only sent over encrypted connections and cannot be read by scripts on the page. The language setting is stored in your browser's local storage and is never sent to us.",
      ],
    },
    {
      heading: "No consent needed",
      paragraphs: [
        "These cookies are strictly necessary to provide the service you have asked for, such as staying signed in, or to remember a choice you have made. Under the ePrivacy rules and the Danish cookie order (cookiebekendtgørelsen § 4, stk. 2), such cookies do not require consent. That is why we do not show a cookie banner.",
      ],
    },
    {
      heading: "No tracking",
      paragraphs: [
        "We use no analytics, no advertising, no third-party cookies and no tracking of any kind. We do not load external fonts or scripts, so no one else can follow you through our site.",
      ],
    },
    {
      heading: "Removing cookies",
      paragraphs: [
        "You can delete cookies and local storage in your browser settings at any time. If you delete the login cookie, you will be signed out; if you delete the language setting, the site will choose a language again.",
      ],
    },
    {
      heading: "Questions",
      paragraphs: [`If you have questions about cookies, ${contactUsElsewhere}. See also the privacy policy.`],
    },
  ],
};

export const en: Record<LegalDocId, LegalDoc> = { privacy, terms, dpa, cookies };
